import React, { useMemo, useState } from "react";
import {
  CardContent,
  Typography,
  Divider,
  Grid2,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Cancel,
  Replay,
  Store,
  LocalShipping,
  HourglassEmpty,
  CheckCircle,
} from "@mui/icons-material";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "../../../hooks/useTranslation";
import { Order } from "../../orders/utils/types";
import { useCustomMutation } from "@refinedev/core";
import { LoadingButton } from "@mui/lab";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { Copyable } from "../../../components/copyable/Copyable";

type Props = {
  order: Order;
};

/* -----------------------------
 * Status UI configuration
 * ----------------------------- */
const supplyOrderStatusConfig: Record<
  string,
  {
    color: "success" | "warning" | "error" | "info" | "default";
    icon: React.ReactElement;
  }
> = {
  PENDING: {
    color: "warning",
    icon: <HourglassEmpty fontSize="small" />,
  },
  CONFIRMED: {
    color: "info",
    icon: <CheckCircle fontSize="small" />,
  },
  SHIPPING: {
    color: "info",
    icon: <LocalShipping fontSize="small" />,
  },
  DELIVERED: {
    color: "success",
    icon: <CheckCircle fontSize="small" />,
  },
  REPACKAGING: {
    color: "warning",
    icon: <Replay fontSize="small" />,
  },
  CANCELLED: {
    color: "error",
    icon: <Cancel fontSize="small" />,
  },
  REFUNDED: {
    color: "error",
    icon: <Replay fontSize="small" />,
  },
  PARTIALLY_REFUNDED: {
    color: "warning",
    icon: <Replay fontSize="small" />,
  },
  STORED_ON_WAREHOUSE: {
    color: "default",
    icon: <Store fontSize="small" />,
  },
  PAYMENT_DECLINED: {
    color: "error",
    icon: <CreditCardOffIcon fontSize="small" />,
  },
};

const NON_CANCELLABLE_STATUSES = new Set([
  "CANCELLED",
  "REFUNDED",
  "PAYMENT_DECLINED",
]);
export const OrderSupplyOrders = ({ order }: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.orderSupplyOrders",
  );
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const { mutate, isLoading } = useCustomMutation();
  const queryClient = useQueryClient();
  const handleCancelClick = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOpenDialog(true);
  };
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 1500);
    });
  };
  const handleCloseDialog = () => {
    if (isLoading) return;
    setOpenDialog(false);
    setSelectedOrderId(null);
  };

  const handleConfirmCancel = () => {
    if (!selectedOrderId) return;

    mutate(
      {
        url: `store/${selectedCountry?.id}/order/${order.id}`,
        method: "put",
        values: {
          supplyOrderStatus: "CANCELLED",
          amazonOrderId: selectedOrderId,
        },
        successNotification: () => ({
          message: translate("supplyOrderCancelled"),
          type: "success",
        }),
        errorNotification: () => ({
          message: translate("supplyOrderCancelFailed"),
          type: "error",
        }),
      },
      {
        onSuccess: () => {
          (async () => {
            handleCloseDialog();

            const queryKey = [
              "order-detail",
              selectedCountry?.id,
              String(order.id),
            ];

            await queryClient.invalidateQueries({ queryKey });

            await queryClient.refetchQueries({
              queryKey,
            });
          })();
        },
        onError: (error) => {
          console.error("Cancel failed", error);
        },
      },
    );
  };
  const supplyOrders = order?.supplyOrders ?? [];

  const supplyOrderItems = useMemo(
    () =>
      supplyOrders.map((supplyOrder, index) => ({
        index: index + 1,
        status: supplyOrder.supplyOrderStatus,
        email: supplyOrder.buyerEmail,
        orderId: supplyOrder.amazonSupplyOrderId,
      })),
    [supplyOrders],
  );

  return (
    <CardContent sx={{ display: "flex", flexDirection: "column" }}>
      <Typography variant="h6">{translate("supplyOrders")}</Typography>
      <Divider sx={{ my: 1 }} />

      <Grid2 container spacing={2}>
        {supplyOrderItems?.map((item, index) => {
          const statusConfig = supplyOrderStatusConfig[item.status];
          const isCancellable =
            item.status && !NON_CANCELLABLE_STATUSES.has(item.status);
          return (
            <Grid2 key={item?.orderId} size={{ xs: 12 }}>
              {index > 0 && <Divider sx={{ mb: 2 }} />}

              {/* CONTENT */}
              <Grid2 container spacing={1} mb={1}>
                {/* Order ID */}
                <Grid2
                  size={{ xs: 12 }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {translate("orderId")}
                  </Typography>
                  <Copyable initialLabel={item?.orderId} />
                </Grid2>

                {/* Status */}
                <Grid2
                  size={{ xs: 12 }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">{translate("status")}</Typography>

                  <Tooltip title={item?.status} arrow>
                    <Chip
                      size="small"
                      icon={statusConfig?.icon}
                      label={translateWithoutPrefix(
                        `common.supplyOrderStatus.${item.status}`,
                      )}
                      color={statusConfig?.color ?? "default"}
                      sx={{ fontWeight: 500 }}
                    />
                  </Tooltip>
                </Grid2>

                {/* Email */}
                <Grid2
                  size={{ xs: 12 }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">{translate("email")}</Typography>
                  <Tooltip
                    title={
                      copiedEmail === item.email
                        ? translateWithoutPrefix("common.copied")
                        : item.email
                    }
                    arrow
                  >
                    <Typography
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation(); // 🔥 önemli (parent click varsa)
                        handleCopyEmail(item.email);
                      }}
                      sx={{
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        transition: "0.2s",
                        "&:hover": {
                          opacity: 0.8,
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {item.email}
                    </Typography>
                  </Tooltip>
                </Grid2>
              </Grid2>
              <Grid2
                size={{ xs: 12 }}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                {isCancellable && (
                  <LoadingButton
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleCancelClick(item.orderId)}
                    loading={isLoading}
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      px: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {translate("cancelSupplyOrder")}
                  </LoadingButton>
                )}
              </Grid2>
            </Grid2>
          );
        })}
      </Grid2>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle> {translate("cancelDialog.title")}</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {translate("cancelDialog.description")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            {translateWithoutPrefix("buttons.close")}
          </Button>

          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading
              ? translate("cancelling")
              : translateWithoutPrefix("buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  );
};
